import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { S as StringService } from './index2.js';
import { D as DomService, T as TrackingService } from './index3.js';
import { T as TranslationService } from './index6.js';

const rocheFiltersCss = ":host{display:block;margin-top:3rem;margin-top:0;display:inline-grid}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 64rem){:host{margin-top:0}}:host .filter-list{display:flex;flex-wrap:nowrap;padding:0;margin:0;list-style-type:none;overflow-x:auto;overflow-y:hidden;width:100%;mask-image:linear-gradient(to left, transparent 0%, black 4rem);-webkit-mask-image:linear-gradient(to left, transparent 0%, black 4rem)}:host .filter{font-family:\"roche-sans\";font-style:normal;font-weight:200;color:var(--color-text--primary);font-size:var(--body--font-size--small-up);line-height:1.8125rem;font-weight:400;display:inline;position:relative;border:1px solid #dbd6d1;padding:0.34375rem 1rem;margin:0 0.75rem 0 0;color:#544f4f;white-space:nowrap}@media (min-width: 64rem){:host .filter{font-size:var(--body--font-size--xlarge-up);line-height:2rem}}@media (min-width: 90rem){:host .filter{font-size:1.125rem;line-height:1.8125rem}}:host .filter:focus,:host .filter:focus-visible{outline:var(--color-interactive--focus) dashed 2px;outline-offset:unset}:host .filter:focus:not(:focus-visible){outline:none}:host .filter:hover,:host .filter--active{background-color:var(--color-interactive--primary-hover);border-color:var(--color-interactive--primary-hover);color:#fff;transition:background-color 200ms ease, border-color 200ms ease, color 200ms ease;text-decoration:none}:host .filter:hover:not(.filter--active){background-color:#eae8e5;border-color:#706b69;color:#544f4f;cursor:pointer}:host .filter::after{content:\"\";height:100%;width:100%;position:absolute;top:-38%;left:0;padding:0.5rem 0}@media (min-width: 20rem){:host .filter::after{top:-28%}}";
const RocheFiltersStyle0 = rocheFiltersCss;

const RocheFilters$1 = /*@__PURE__*/ proxyCustomElement(class RocheFilters extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.filterUpdateEvent = createEvent(this, "rocheFiltersUpdate", 7);
        this.activeFilterIndex = 0;
        this.canInitialize = false;
        this.filterBy = 'tag';
        this.noFilterableContentFound = true;
        this.observeDomChanges = false;
        this.refinedQuery = '';
        this.filters = [];
        this.translations = undefined;
    }
    filterSiblings(activeIndex, oldIndex) {
        if (activeIndex === oldIndex) {
            return;
        }
        this.filterUpdateEvent.emit(activeIndex > 0 ? this.filters[activeIndex] : undefined);
        const [, ...siblings] = Array.from(this.element.parentElement.querySelectorAll('*'));
        siblings.forEach((element) => { element.removeAttribute('hidden'); });
        if (activeIndex <= 0) {
            return;
        }
        siblings
            .filter((element) => element.getAttribute(this.filterBy) !== this.filters[activeIndex])
            .forEach((element) => { element.setAttribute('hidden', ''); });
    }
    initialize() {
        this.filters = this.parsedFilters;
        /*
         * In the event your component has a load more functionality:
         * Notice that index zero matches against all, so we are already preventing redundant "reinits"
         */
        this.filterSiblings(this.activeFilterIndex, 0);
    }
    get items() {
        return Array.from(this.element.parentElement.querySelectorAll(`[${this.filterBy}]${this.refinedQuery}`));
    }
    /*
     * We use getAttribute because:
     * 1. It's faster (https://www.measurethat.net/Benchmarks/ShowResult/183934)
     * 2. Runs on test environment as it's implemented by Jest's MockHTMLElement
     */
    get parsedFilters() {
        var _a, _b;
        return Array.from(new Set(
        /**
         * Using the nullish coalescing operator here is just a temporary
         * workaround. There is currently a race condition between `initialize`
         * and `componentWillLoad`.  When `initialize` (and hence `parsedFilters`)
         * is run before `componentWillLoad`, `this.translations` is still
         * undefined.
         *
         * TODO: Fix the race condition.
         */
        [StringService.capitalize((_b = (_a = this.translations) === null || _a === void 0 ? void 0 : _a.all) !== null && _b !== void 0 ? _b : 'all'),
            ...this.items.map((node) => node.getAttribute(this.filterBy))]));
    }
    // eslint-disable-next-line class-methods-use-this
    get defaultTracking() {
        return {
            event: 'filter_click',
            component_name: 'roche-filters',
        };
    }
    async componentWillLoad() {
        var _a;
        this.translations = {
            all: await TranslationService.translate('all'),
        };
        this.updateActiveFilter = this.updateActiveFilter.bind(this);
        if (this.observeDomChanges && !((_a = DomService.isMockedContext) === null || _a === void 0 ? void 0 : _a.call(DomService))) {
            this.observer = new MutationObserver(() => {
                this.filters = this.parsedFilters;
            });
            this.observer.observe(this.element.parentElement, { childList: true });
        }
    }
    render() {
        if (this.filters.length <= 2) {
            this.noFilterableContentFound = true;
            return null;
        }
        this.noFilterableContentFound = false;
        const classes = {
            'filter-list': true,
        };
        return (h(Host, null, h("ul", { class: classes }, this.filters.map((filterValue, index) => (h("li", { onClick: () => this.updateActiveFilter(index), key: index, class: `filter ${index === this.activeFilterIndex ? 'filter--active' : ''}` }, filterValue))))));
    }
    updateActiveFilter(index) {
        this.activeFilterIndex = index;
        this.trackActiveFilter();
    }
    trackActiveFilter() {
        TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { additionalParameters: {
                active_filter_index: this.activeFilterIndex,
                active_filter_name: this.filters[this.activeFilterIndex],
            } }));
    }
    componentWillUnmount() {
        this.observer.disconnect();
    }
    get element() { return this; }
    static get watchers() { return {
        "activeFilterIndex": ["filterSiblings"],
        "canInitialize": ["initialize"]
    }; }
    static get style() { return RocheFiltersStyle0; }
}, [1, "roche-filters", {
        "activeFilterIndex": [1538, "active-filter-index"],
        "canInitialize": [4, "can-initialize"],
        "filterBy": [1, "filter-by"],
        "noFilterableContentFound": [1540, "no-filterable-content-found"],
        "observeDomChanges": [4, "observe-dom-changes"],
        "refinedQuery": [1, "refined-query"],
        "filters": [32],
        "translations": [32]
    }, undefined, {
        "activeFilterIndex": ["filterSiblings"],
        "canInitialize": ["initialize"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-filters"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-filters":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheFilters$1);
            }
            break;
    } });
}

const RocheFilters = RocheFilters$1;
const defineCustomElement = defineCustomElement$1;

export { RocheFilters, defineCustomElement };

//# sourceMappingURL=roche-filters.js.map